import { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { m } from 'framer-motion';
// @mui
import {
    Stack,
    MenuItem,
    IconButton,
    Box,
    Typography,
    Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// components
import { dispatch } from 'src/redux/store';
import { updateSettingsMenuActive } from 'src/redux/slices/mergeTags';
import { Iconify, MenuPopover } from '../../../components';
import { varHover } from '../../../components/animate';
import { MENU_OPTIONS } from '../../../constants/SettingsMenuOptions';
import { useGetCurrentUser } from '../../../hooks';
import useIsScopedAdmin from '../../../hooks/useIsScopedAdmin';
import { getMenuOptions } from '../../../utils/settingUtils';

export default function SettingsPopover() {
    const [open, setOpen] = useState(null);
    const { isSuperUser } = useGetCurrentUser();
    const isScopedAdmin = useIsScopedAdmin();
    const theme = useTheme();

    const handleClick = () => {
        dispatch(updateSettingsMenuActive(true));
        setOpen(null);
    };

    // Group menu options by their `group` field, preserving order
    const groupedOptions = useMemo(() => {
        const filtered = getMenuOptions(MENU_OPTIONS, isSuperUser, { isScopedAdmin }) || [];
        const groups = [];
        const seen = new Set();
        filtered.forEach((option) => {
            const groupName = option.group || 'Other';
            if (!seen.has(groupName)) {
                seen.add(groupName);
                groups.push({ name: groupName, items: [] });
            }
            groups.find((g) => g.name === groupName).items.push(option);
        });
        return groups;
    }, [isSuperUser, isScopedAdmin]);

    return (
        <>
            <IconButton
                component={m.button}
                whileTap="tap"
                whileHover="hover"
                variants={varHover(1.05)}
                aria-label="settings"
                onClick={(event) => setOpen(event.currentTarget)}
                sx={{ width: 40, height: 40 }}
                id='setting-icon'
            >
                <Iconify icon="solar:settings-bold-duotone" width={24} />
            </IconButton>
            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={() => setOpen(null)}
                id='setting-menu'
                sx={{
                    p: 0,
                    mt: 1.5,
                    ml: 0.75,
                    width: 320,
                    maxHeight: '80vh',
                    overflow: 'auto',
                    '& .MuiMenuItem-root': {
                        borderRadius: '8px',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: theme.palette.text.primary }}>
                        Settings
                    </Typography>
                </Box>
                <Divider />

                {/* Grouped items */}
                <Stack sx={{ p: 1 }}>
                    {groupedOptions.map((group, groupIndex) => (
                        <Box key={group.name}>
                            {groupIndex > 0 && <Divider sx={{ my: 0.5, mx: 1 }} />}
                            <Typography
                                sx={{
                                    px: 1.5,
                                    pt: groupIndex === 0 ? 0.5 : 1,
                                    pb: 0.5,
                                    fontSize: '0.6875rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    color: theme.palette.text.disabled,
                                }}
                            >
                                {group.name}
                            </Typography>
                            {group.items.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    to={option.linkTo}
                                    component={RouterLink}
                                    onClick={handleClick}
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        minHeight: 'auto',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        },
                                        '&:hover .settings-icon-box': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            '& svg': { color: theme.palette.primary.main },
                                        },
                                    }}
                                >
                                    <Box
                                        className="settings-icon-box"
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha(theme.palette.grey[500], 0.08),
                                            mr: 1.5,
                                            flexShrink: 0,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <Iconify
                                            icon={option.icon}
                                            width={20}
                                            sx={{ color: theme.palette.text.secondary, transition: 'color 0.2s' }}
                                        />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.8125rem',
                                                fontWeight: 600,
                                                color: theme.palette.text.primary,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {option.label}
                                        </Typography>
                                        {option.description && (
                                            <Typography
                                                sx={{
                                                    fontSize: '0.6875rem',
                                                    color: theme.palette.text.secondary,
                                                    lineHeight: 1.3,
                                                    whiteSpace: 'normal',
                                                }}
                                            >
                                                {option.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Box>
                    ))}
                </Stack>
            </MenuPopover>
        </>
    );
}
